import { useState } from "react";
import { Smartphone, Download, Share2, Plus, Check, Chrome, Apple } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { toast } from "sonner";

/**
 * Smart, always-visible "Download App" CTA — works on every platform.
 *
 *  - Android Chrome with prompt queued     → fires native install
 *  - Android Chrome without prompt yet     → dialog with "Add to Home screen" steps
 *  - iOS Safari                            → dialog with Safari share-sheet steps
 *  - Desktop Chrome / Edge                 → dialog with install-via-menu steps
 *  - Already installed (standalone)        → friendly confirmation
 *
 * Renders both a prominent section on the landing page and accepts an
 * optional `compact` prop to use the same logic as a smaller button.
 */
function platformSteps({ isIos, isStandalone }) {
  if (isStandalone) {
    return {
      title: "You're already on the app!",
      icon: Check,
      steps: ["Shakti Wings is installed and running in app mode. Enjoy faster loading and home-screen access."],
    };
  }
  if (isIos) {
    return {
      title: "Install on iPhone / iPad (Safari)",
      icon: Apple,
      steps: [
        "Tap the Share button (square with arrow ↑) at the bottom of Safari.",
        "Scroll down and tap 'Add to Home Screen'.",
        "Tap 'Add' in the top-right corner.",
        "Open Shakti Wings from your home screen like a native app.",
      ],
    };
  }
  return {
    title: "Install on Android / Desktop (Chrome / Edge)",
    icon: Chrome,
    steps: [
      "Tap the ⋮ menu (top-right) in Chrome / Edge.",
      "Tap 'Install app' or 'Add to Home screen'.",
      "Confirm 'Install' — Shakti Wings will appear on your home screen.",
      "Open it like any other app — no app store needed!",
    ],
  };
}

export function DownloadAppButton({ className = "", size = "lg", testId = "download-app-btn" }) {
  const { canInstall, isIos, isStandalone, install } = usePwaInstall();
  const [open, setOpen] = useState(false);

  const handleClick = async () => {
    if (canInstall) {
      const res = await install();
      if (res?.outcome === "accepted") {
        toast.success("Shakti Wings is being installed!");
      } else if (res?.outcome === "dismissed") {
        toast.info("Tap 'Download App' again anytime to install.");
      }
      return;
    }
    setOpen(true);
  };

  const info = platformSteps({ isIos, isStandalone });
  const Icon = info.icon;

  const sizeClass = size === "lg"
    ? "px-6 py-3.5 text-base"
    : size === "sm"
      ? "px-3 py-1.5 text-xs"
      : "px-4 py-2 text-sm";

  return (
    <>
      <button
        onClick={handleClick}
        data-testid={testId}
        className={`inline-flex items-center gap-2 rounded-full font-semibold bg-gradient-to-r from-magenta-600 to-magenta-700 text-white hover:from-magenta-700 hover:to-magenta-800 shadow-lg shadow-magenta-500/20 hover:shadow-magenta-500/30 transition-all ${sizeClass} ${className}`}
        aria-label="Download Shakti Wings app"
      >
        <Download className="w-4 h-4" strokeWidth={2.4} />
        {isStandalone ? "App Installed" : "Download App"}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-magenta-500 to-amethyst-600 text-white flex items-center justify-center mb-3 shadow-lg">
              <Icon className="w-7 h-7" strokeWidth={1.8} />
            </div>
            <DialogTitle className="font-serif text-xl">{info.title}</DialogTitle>
            <DialogDescription>
              Shakti Wings is a Progressive Web App — install it once, use it offline, just like a native app from the app store.
            </DialogDescription>
          </DialogHeader>
          <ol className="text-sm space-y-3 text-gray-700 pt-2">
            {info.steps.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-magenta-100 text-magenta-700 text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
          {!isStandalone && (
            <div className="bg-gold-50 border border-gold-200 rounded-xl p-3 mt-2 flex items-start gap-2">
              <Plus className="w-4 h-4 text-gold-700 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amethyst-900 leading-relaxed">
                <span className="font-semibold">Pro tip:</span> Once installed, the app loads instantly and you&apos;ll get notifications about new products, events, and Shakti Coin rewards.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function DownloadAppCTA() {
  const { isStandalone } = usePwaInstall();
  if (isStandalone) return null;
  return (
    <section className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="download-app-section">
      <div className="max-w-5xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amethyst-900 via-amethyst-800 to-magenta-900 px-6 py-8 sm:px-12 sm:py-12 lg:py-14 shadow-2xl">
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gold-400/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-magenta-500/20 blur-3xl pointer-events-none" />

          <div className="relative grid sm:grid-cols-[1fr_auto] gap-6 sm:gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-gold-300 text-xs font-semibold uppercase tracking-wider mb-3">
                <Smartphone className="w-3.5 h-3.5" />
                Mobile App Available
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold text-white tracking-tight leading-tight">
                Shakti Wings, hamesha aapke saath.
              </h2>
              <p className="mt-3 text-sm sm:text-base text-amethyst-100 leading-relaxed max-w-xl">
                Apne phone ki home screen pe Shakti Wings install karein — faster shopping, instant order updates, and Shakti Coin alerts. Bilkul free, no app store needed.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <DownloadAppButton size="lg" />
                <div className="flex items-center gap-1.5 text-xs text-amethyst-200">
                  <Check className="w-3.5 h-3.5 text-gold-300" />
                  <span>Works offline</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-amethyst-200">
                  <Check className="w-3.5 h-3.5 text-gold-300" />
                  <span>Push notifications</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-amethyst-200">
                  <Check className="w-3.5 h-3.5 text-gold-300" />
                  <span>Home-screen icon</span>
                </div>
              </div>
            </div>

            {/* Decorative phone mockup */}
            <div className="hidden sm:flex flex-col items-center gap-3">
              <div className="relative w-32 h-56 rounded-[2rem] bg-gradient-to-br from-amethyst-700 to-amethyst-900 p-2 shadow-2xl border-4 border-amethyst-700 ring-1 ring-amethyst-600/40">
                <div className="w-full h-full rounded-[1.5rem] bg-gradient-to-br from-magenta-500 via-magenta-600 to-amethyst-700 flex flex-col items-center justify-center text-white p-2">
                  <div className="text-3xl mb-1">🪷</div>
                  <p className="text-[10px] font-serif font-semibold">Shakti Wings</p>
                  <p className="text-[8px] text-amethyst-100">Community App</p>
                </div>
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-12 h-1 rounded-full bg-amethyst-900" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-gold-300" />
                <span className="w-1.5 h-1.5 rounded-full bg-amethyst-300" />
                <span className="w-1.5 h-1.5 rounded-full bg-amethyst-400/50" />
              </div>
              <Share2 className="w-4 h-4 text-amethyst-200" />
              <p className="text-[10px] text-amethyst-200 text-center max-w-[8rem] leading-tight">Share with sisters & build the community</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
